import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, Copy, RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllSyncLogs } from '../api/syncLogs';
// --- Types ---
interface SyncLog {
  id: string;
  syncStartedAt: string;
  syncCompletedAt?: string;
  status: 'completed' | 'in_progress' | 'failed';
  recordsAttempted: number;
  recordsUploaded: number;
  recordsFailed: number;
  errorMessage?: string;
  device?: {
    id: string;
    deviceUid: string;
    deviceType: string;
  };
  sessionId?: string;
}
// --- Main Component ---
export function SyncLogs() {
  const {
    token
  } = useAuth();
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  // Modals & Panels State
  const [viewLog, setViewLog] = useState<SyncLog | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Live Timer State
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);
  // Fetch Data
  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        status: statusFilter === 'all' ? undefined : statusFilter,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        limit
      };
      const res = await getAllSyncLogs(token, filters);
      setLogs(res.logs || []);
      setTotalLogs(res.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load sync logs');
    } finally {
      setIsLoading(false);
    }
  }, [token, debouncedSearch, statusFilter, dateFrom, dateTo, page, limit]);
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  const showToast = (message: string) => {
    setToast({
      message,
      type: 'success'
    });
    setTimeout(() => setToast(null), 3000);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  return <div className="relative min-h-full pb-20" data-id="element-909">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-id="element-910">
        <div data-id="element-911">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-912">
            Device Sync Logs
          </h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-913">
            {isLoading ? 'Loading...' : `${totalLogs.toLocaleString()} total sync logs`}
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap" data-id="element-914">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-915">
          <div className="relative" data-id="element-916">
            <Search className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-917" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search device UID..." className="w-[240px] h-[36px] bg-surface border border-border rounded-input pl-9 pr-4 text-[14px] text-white placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" data-id="element-918" />
          </div>

          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent appearance-none pr-8 relative" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center'
        }} data-id="element-919">
            <option value="all" data-id="element-920">All Status</option>
            <option value="completed" data-id="element-921">Completed</option>
            <option value="in_progress" data-id="element-922">In Progress</option>
            <option value="failed" data-id="element-923">Failed</option>
          </select>

          <div className="flex items-center gap-2" data-id="element-924">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent [color-scheme:dark]" data-id="element-925" />
            <span className="text-text-secondary text-[14px]" data-id="element-926">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-[36px] bg-surface border border-border rounded-input px-3 text-[14px] text-white focus:outline-none focus:border-accent [color-scheme:dark]" data-id="element-927" />
          </div>
        </div>

        <div className="text-[13px] text-text-secondary" data-id="element-928">
          Showing {logs.length > 0 ? (page - 1) * limit + 1 : 0}-
          {Math.min(page * limit, totalLogs)} of {totalLogs}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-border rounded-card overflow-hidden" data-id="element-929">
        <div className="overflow-x-auto" data-id="element-930">
          <table className="w-full text-left border-collapse" data-id="element-931">
            <thead data-id="element-932">
              <tr className="bg-surface-elevated border-b border-border h-[44px]" data-id="element-933">
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[80px]" data-id="element-934">
                  Log ID
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[160px]" data-id="element-935">
                  Device
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-936">
                  Session
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[160px]" data-id="element-937">
                  Started
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[100px]" data-id="element-938">
                  Duration
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[120px]" data-id="element-939">
                  Status
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[140px]" data-id="element-940">
                  Records
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[200px]" data-id="element-941">
                  Error
                </th>
                <th className="px-4 text-[12px] uppercase font-semibold text-text-secondary tracking-[1px] w-[60px]" data-id="element-942">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-943">
              {isLoading ? <tr data-id="element-944">
                  <td colSpan={9} className="h-[200px] text-center text-text-secondary" data-id="element-945">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" data-id="element-946" />
                    Loading sync logs...
                  </td>
                </tr> : logs.length === 0 ? <tr data-id="element-947">
                  <td colSpan={9} className="h-[200px] text-center text-text-secondary" data-id="element-948">
                    No sync logs found matching your criteria.
                  </td>
                </tr> : logs.map(log => <tr key={log.id} className="h-[56px] border-b border-[#1E1E1E] hover:bg-[#1E1E1E] transition-colors" data-id="element-949">
                    <td className="px-4" data-id="element-950">
                      <div className="flex items-center gap-2" data-id="element-951">
                        <span className="text-[13px] font-mono text-white" data-id="element-952">
                          {log.id.substring(0, 8)}
                        </span>
                        <button onClick={() => copyToClipboard(log.id)} className="text-text-tertiary hover:text-white transition-colors" data-id="element-953">
                          <Copy className="w-3 h-3" data-id="element-954" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4" data-id="element-955">
                      {log.device ? <div className="flex flex-col" data-id="element-956">
                          <span className="text-[13px] font-mono text-white" data-id="element-957">
                            {log.device.deviceUid}
                          </span>
                          <span className="text-[11px] text-text-secondary" data-id="element-958">
                            {log.device.deviceType || 'Bella Belt'}
                          </span>
                        </div> : <span className="text-[13px] text-text-tertiary" data-id="element-959">
                          —
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-960">
                      {log.sessionId ? <div className="flex items-center gap-2" data-id="element-961">
                          <span className="text-[13px] font-mono text-white" data-id="element-962">
                            {log.sessionId.substring(0, 8)}
                          </span>
                          <button onClick={() => copyToClipboard(log.sessionId!)} className="text-text-tertiary hover:text-white transition-colors" data-id="element-963">
                            <Copy className="w-3 h-3" data-id="element-964" />
                          </button>
                        </div> : <span className="text-[13px] text-text-tertiary" data-id="element-965">
                          —
                        </span>}
                    </td>
                    <td className="px-4 text-[13px] text-text-secondary" data-id="element-966">
                      {new Date(log.syncStartedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
                    </td>
                    <td className="px-4" data-id="element-967">
                      {log.status === 'in_progress' ? <span className="text-[13px] text-warning font-medium" data-id="element-968">
                          {formatDuration(log.syncStartedAt)}
                        </span> : log.status === 'completed' || log.status === 'failed' ? <span className="text-[13px] text-text-secondary" data-id="element-969">
                          {formatDuration(log.syncStartedAt, log.syncCompletedAt)}
                        </span> : <span className="text-[13px] text-text-tertiary" data-id="element-970">
                          —
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-971">
                      {log.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-972">
                          Completed
                        </span>}
                      {log.status === 'in_progress' && <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-973">
                          <div className="w-2.5 h-2.5 rounded-full border-2 border-warning border-t-transparent animate-spin" data-id="element-974" />
                          In Progress
                        </div>}
                      {log.status === 'failed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-975">
                          Failed
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-976">
                      <div className="flex items-center gap-1.5 text-[13px]" data-id="element-977">
                        <span className="text-success font-medium" data-id="element-978">
                          {log.recordsUploaded}
                        </span>
                        <span className="text-text-tertiary" data-id="element-979">/</span>
                        <span className="text-text-secondary" data-id="element-980">
                          {log.recordsAttempted}
                        </span>
                        {log.recordsFailed > 0 && <>
                            <span className="text-text-tertiary" data-id="element-981">/</span>
                            <span className="text-danger font-medium" data-id="element-982">
                              {log.recordsFailed}
                            </span>
                          </>}
                      </div>
                    </td>
                    <td className="px-4" data-id="element-983">
                      {log.errorMessage ? <p className="text-[13px] text-danger truncate max-w-[180px]" title={log.errorMessage} data-id="element-984">
                          {log.errorMessage}
                        </p> : <span className="text-[13px] text-text-tertiary" data-id="element-985">
                          —
                        </span>}
                    </td>
                    <td className="px-4" data-id="element-986">
                      <button onClick={() => setViewLog(log)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-border transition-colors text-text-tertiary hover:text-white" data-id="element-987">
                        <Eye className="w-4 h-4" data-id="element-988" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4" data-id="element-989">
        <div className="flex items-center gap-2" data-id="element-990">
          <select value={limit} onChange={e => {
          setLimit(Number(e.target.value));
          setPage(1);
        }} className="h-[32px] bg-surface border border-border rounded-btn px-2 text-[13px] text-white focus:outline-none focus:border-accent" data-id="element-991">
            <option value={25} data-id="element-992">25 per page</option>
            <option value={50} data-id="element-993">50 per page</option>
            <option value={100} data-id="element-994">100 per page</option>
          </select>
        </div>

        <div className="flex items-center gap-2" data-id="element-995">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-996">
            <ChevronLeft className="w-4 h-4" data-id="element-997" /> Prev
          </button>

          <button className="w-[32px] h-[32px] bg-accent text-background rounded-btn text-[13px] font-medium flex items-center justify-center" data-id="element-998">
            {page}
          </button>

          <button onClick={() => setPage(p => p + 1)} disabled={page * limit >= totalLogs} className="h-[32px] px-3 bg-surface border border-border rounded-btn text-[13px] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-border transition-colors flex items-center gap-1" data-id="element-999">
            Next <ChevronRight className="w-4 h-4" data-id="element-1000" />
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className="fixed top-6 right-6 px-4 py-3 rounded-badge shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top-5 bg-success text-white" data-id="element-1001">
          <span className="text-[14px] font-medium" data-id="element-1002">{toast.message}</span>
        </div>}

      {/* Detail Panel */}
      <SyncLogDetailPanel log={viewLog} onClose={() => setViewLog(null)} now={now} data-id="element-1003" />
    </div>;
}
// --- Subcomponents ---
function SyncLogDetailPanel({
  log,
  onClose,
  now
}: {
  log: SyncLog | null;
  onClose: () => void;
  now: Date;
}) {
  if (!log) return null;
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-1004">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-1005" />
      <div className="relative w-[400px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-1006">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-1007">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-1008">
            <X className="w-5 h-5" data-id="element-1009" />
          </button>
          <div className="flex items-center gap-4" data-id="element-1010">
            <div className="w-12 h-12 rounded-full bg-[rgba(245,158,11,0.1)] flex items-center justify-center flex-shrink-0" data-id="element-1011">
              <RefreshCw className="w-6 h-6 text-warning" data-id="element-1012" />
            </div>
            <div data-id="element-1013">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-1014">
                Sync Log Detail
              </h2>
              <p className="text-[13px] text-text-secondary font-mono mt-1" data-id="element-1015">
                {log.id}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-1016">
          {/* Info */}
          <div className="space-y-4" data-id="element-1017">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1018">
              Information
            </h3>
            <div className="space-y-3" data-id="element-1019">
              <div className="flex justify-between items-center" data-id="element-1020">
                <span className="text-[13px] text-text-secondary" data-id="element-1021">Status</span>
                {log.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-1022">
                    Completed
                  </span>}
                {log.status === 'in_progress' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-1023">
                    In Progress
                  </span>}
                {log.status === 'failed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-1024">
                    Failed
                  </span>}
              </div>
              <div className="flex justify-between" data-id="element-1025">
                <span className="text-[13px] text-text-secondary" data-id="element-1026">Started</span>
                <span className="text-[13px] text-white" data-id="element-1027">
                  {new Date(log.syncStartedAt).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1028">
                <span className="text-[13px] text-text-secondary" data-id="element-1029">
                  Completed
                </span>
                <span className="text-[13px] text-white" data-id="element-1030">
                  {log.syncCompletedAt ? new Date(log.syncCompletedAt).toLocaleString() : '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1031">
                <span className="text-[13px] text-text-secondary" data-id="element-1032">
                  Duration
                </span>
                <span className="text-[13px] text-white" data-id="element-1033">
                  {formatDuration(log.syncStartedAt, log.syncCompletedAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Records Stats */}
          <div className="space-y-4" data-id="element-1034">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1035">
              Records
            </h3>
            <div className="grid grid-cols-3 gap-3" data-id="element-1036">
              <div className="bg-background border border-border rounded-card p-3 text-center" data-id="element-1037">
                <p className="text-[11px] text-text-secondary uppercase mb-1" data-id="element-1038">
                  Attempted
                </p>
                <p className="text-[18px] font-semibold text-white" data-id="element-1039">
                  {log.recordsAttempted}
                </p>
              </div>
              <div className="bg-background border border-[rgba(34,197,94,0.3)] rounded-card p-3 text-center" data-id="element-1040">
                <p className="text-[11px] text-success uppercase mb-1" data-id="element-1041">
                  Uploaded
                </p>
                <p className="text-[18px] font-semibold text-success" data-id="element-1042">
                  {log.recordsUploaded}
                </p>
              </div>
              <div className={`bg-background border rounded-card p-3 text-center ${log.recordsFailed > 0 ? 'border-[rgba(239,68,68,0.3)]' : 'border-border'}`} data-id="element-1043">
                <p className={`text-[11px] uppercase mb-1 ${log.recordsFailed > 0 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-1044">
                  Failed
                </p>
                <p className={`text-[18px] font-semibold ${log.recordsFailed > 0 ? 'text-danger' : 'text-white'}`} data-id="element-1045">
                  {log.recordsFailed}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {log.errorMessage && <div className="space-y-4" data-id="element-1046">
              <h3 className="text-[12px] uppercase font-semibold text-danger tracking-[1px] flex items-center gap-2" data-id="element-1047">
                <AlertTriangle className="w-3 h-3" data-id="element-1048" /> Error Details
              </h3>
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-[8px] p-3 max-h-[200px] overflow-y-auto" data-id="element-1049">
                <pre className="text-[12px] text-danger font-mono whitespace-pre-wrap break-words" data-id="element-1050">
                  {log.errorMessage}
                </pre>
              </div>
            </div>}

          {/* Entities */}
          <div className="space-y-4" data-id="element-1051">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1052">
              Related Entities
            </h3>

            {log.device && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-1053">
                <div data-id="element-1054">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-1055">Device</p>
                  <p className="text-[13px] text-white font-mono" data-id="element-1056">
                    {log.device.deviceUid}
                  </p>
                  <p className="text-[11px] text-text-tertiary" data-id="element-1057">
                    {log.device.deviceType || 'Bella Belt'}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-1058">
                  View
                </button>
              </div>}

            {log.sessionId && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-1059">
                <div data-id="element-1060">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-1061">
                    Session
                  </p>
                  <p className="text-[13px] text-white font-mono" data-id="element-1062">
                    {log.sessionId.substring(0, 8)}...
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-1063">
                  View Session
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}